<template>
  <aside
    :class="['side-menu-base', { collapsed }]"
    :style="sideMenuStyle"
  >
    <div class="menu-content">
      <div class="collapse-container" :class="{ collapsed }">
        <span v-if="!collapsed" class="collapse-label">Hide Menu</span>
        <div class="button-column">
          <button class="collapse-toggle" @click="sideMenuState.toggle()">
            {{ collapsed ? '›' : '‹' }}
          </button>
          <div v-if="collapsed" class="show-label">Show Menu</div>
        </div>
      </div>

      <div
        v-for="group in menuGroups"
        :key="group.title"
        class="menu-group"
        v-show="!collapsed"
      >
        <h3 class="group-title">{{ group.title }}</h3>
        <ul class="menu-list">
          <li
            v-for="item in group.items"
            :key="item.label"
            class="menu-item"
          >
            <RouterLink
              :to="item.path"
              class="menu-link"
              :class="{
                disabled: item.requiredPermission && !auth.hasPermission(item.requiredPermission),
                'active-link': !isMobile && isActive(item)
              }"
              :active-class="!isMobile ? 'active-link' : ''"
              :tabindex="item.requiredPermission && !auth.hasPermission(item.requiredPermission) ? -1 : 0"
              @click="handleMenuItemClick"
            >
              <span class="menu-label">{{ item.label }}</span>
              <img
                v-if="item.requiredPermission && !auth.hasPermission(item.requiredPermission)"
                src="@/assets/lock-red.png"
                alt="Locked"
                class="lock-icon"
              />
            </RouterLink>

          </li>
        </ul>
      </div>
    </div>

    <button v-if="!collapsed" class="signout-button" @click="signOut">
      Sign Out
    </button>
  </aside>
</template>

<script setup>
import { ref, inject, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

defineProps({
  menuGroups: {
    type: Array,
    required: true
  }
})

const sideMenuState = inject('sideMenuState')
const collapsed = sideMenuState.collapsed

const isMobile = inject('isMobile')
const auth = inject('auth')

// Compute style for mobile expanded menu width
const mobileExpandedStyle = computed(() => {
  if (isMobile?.value && !collapsed.value) {
    return {
      width: '100%',
      position: 'fixed',  // optional: keep it fixed on mobile when expanded
      top: '60px',
      left: '0',
      zIndex: '1000',
      backgroundColor: '#f4f6f9',
      height: 'calc(100vh - 60px)',
      overflowY: 'auto'
    }
  }
  return {}
})

const mobileCollapsedStyle = computed(() => {
  if (isMobile?.value && collapsed.value) {
    return {
      width: '0',
      padding: '0',
      overflow: 'hidden',
      border: 'none',
    }
  }
  return {}
})

const sideMenuStyle = computed(() => {
  return {
    ...mobileExpandedStyle.value,
    ...mobileCollapsedStyle.value,
  }
})

const isActive = (item) => {
  if (!route) return false
  // Check if the current route is exactly the item path
  if (route.path === item.path) return true
  // Check if the current route is a nested route under item.path
  // Add trailing slash to avoid partial matches (e.g., /operations/schedule)
  return route.path.startsWith(item.path + '/')
}



const route = useRoute()
const router = useRouter()

const signOut = async () => {
  const confirmed = window.confirm('Are you sure you want to sign out?')

  if (!confirmed) return

  try {
    const res = await fetch(`${process.env.VUE_APP_API_BASE}/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    })

    localStorage.removeItem('session_token')

    const res_data = await res.json()
    console.log = res_data.message
    
  } catch (err) {
    console.log(err)
  }

  router.push({ name: 'login' })
}

function handleMenuItemClick() {
  // Collapse side menu on mobile only (optional)
  if (isMobile?.value) {
    sideMenuState.collapse()
  }
}

</script>

<style scoped>
.side-menu-base {
  width: 210px;
  background-color: #f4f6f9;
  padding: 0.1rem 1rem;
  border-right: 1px solid #ddd;
  height: calc(100vh - 60px);
  overflow-y: hidden;
  box-sizing: border-box;
  position: sticky;
  top: 60px;
  transition: width 0.3s ease, padding 0.3s ease;
  display: flex;
  flex-direction: column;
  z-index: 1;
}

.side-menu-base.collapsed {
  width: 38px;
  padding: 0.5rem;
}

.menu-content {
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.collapse-container {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: right;
  position: relative;
}

.collapse-container.collapsed {
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.collapse-label {
  font-size: 0.85rem;
  color: #333;
  padding-right: 0.3rem;
}

.collapse-toggle {
  background: none;
  /* border: none; */
  padding-left: 0.3rem;
  padding-right: 0.3rem;
  font-size: 1.2rem;
  cursor: pointer;
  color: #444;
}

.show-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 0.75rem;
  color: #333;
  margin-top: 0.5rem;
}

.button-column {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.menu-group {
  margin-bottom: 1.5rem;
}

.group-title {
  font-size: 0.9rem;
  font-weight: bold;
  color: #555;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-item {
  margin-bottom: 0.1rem;
}

.menu-link {
  display: flex;
  justify-content: space-between; /* space between text and icon */
  align-items: center;
  padding: 0.1rem 0.75rem;
  color: #1e538d;
  border-radius: 4px;
  transition: background-color 0.2s ease, color 0.2s ease;
}


.menu-link:hover {
  background-color: #c1931d;
  color: #ffffff;
}

.active-link {
  background-color: #daa521;
  color: #000;
  font-weight: bold;
}

.menu-link.active-link:hover {
  background-color: #c1931d;
}

.signout-button {
  background-color: #1e538d;
  color: #fff;
  padding: 0.3rem 1rem;
  margin-bottom: 0.8rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.menu-link.disabled {
  pointer-events: none; /* prevent clicking */
  opacity: 0.5;
  cursor: not-allowed;
}

.lock-icon {
  width: 16px;
  height: 16px;
  padding-bottom: 0.1rem;
  margin-left: 6px; /* optional spacing from label */
}

.menu-label {
  flex-grow: 1;
  text-align: left; /* label stays left */
}


</style>
